
(function() {
  'use strict';

  const CONFIG = {
    mapboxToken: 'pk.eyJ1IjoiZXZlbnNpc2Z0dyIsImEiOiJjbHJmMm52eDUwMHI5MmpwZmpiYTVkbHZmIn0.8aciVa66yGXCkNJUZuXE6w',
    defaultRadius: 30,
    mapCenters: {
      uk: { center: [-2.359, 54.3813], zoom: 4.8 },
      ie: { center: [-8.2439, 53.4129], zoom: 6 },
      de: { center: [10.4515, 51.1657], zoom: 5.5 },
      turkey: { center: [35.2433, 38.9637], zoom: 4.8 }
    },
    responsiveZoom: {
      mobile: { uk: 3.9, ie: 5.2, de: 4.5, turkey: 3.8 },
      tablet: { uk: 4.2, ie: 5.3, de: 4.8, turkey: 4.3 },
      desktop: { uk: 4.8, ie: 6, de: 5.5, turkey: 4.8 }
    }
  };

  function getConfig() {
    if (window.clinicFinderConfig) {
      return window.clinicFinderConfig;
    }
    return {
      currentMarket: 'uk',
      customiserApi: 'https://shop.sons.co.uk',
      storeId: '1'
    };
  }

  const config = getConfig();

  // State Management
  const state = {
    allClinics: [],
    turkeyClinics: [],
    selectedCountry: 'default',
    selectedClinic: null,
    userLocation: null,
    radius: CONFIG.defaultRadius,
    loading: false,
    map: null,
    clinicRefs: {},
    currentMarket: config.currentMarket,
    storeId: config.storeId,
    customiserApi: config.customiserApi,
    geocodeTimeout: null
  };

  // Utility: Get Current Store Code (de, ie, uk)
  function getCurrentStore() {
    return state.currentMarket;
  }

  // Initialize Mapbox GL
  function initMapbox() { // NOSONAR
    if (!window.mapboxgl) {
      console.error('Mapbox GL not loaded');
      return;
    }

    // NOSONAR
    window.mapboxgl.accessToken = CONFIG.mapboxToken;

    const mapContainer = document.getElementById('map');
    if (!mapContainer) {
      console.error('Map container not found');
      return;
    }

    const initialZoom = getResponsiveZoom();
    const countryConfig = getCountryConfig(state.selectedCountry);

    try {
      // NOSONAR
      state.map = new window.mapboxgl.Map({
        container: mapContainer,
        style: 'mapbox://styles/mapbox/light-v11',
        center: countryConfig.center,
        zoom: initialZoom
      });

      state.map.on('load', onMapLoad);
      window.addEventListener('resize', handleMapResize);
    } catch (error) {
      console.error('Error initializing Mapbox:', error);
    }
  }

  // Handle Map Load
  function onMapLoad() {
    console.log('Map loaded');
    if (!state.map) return;

    // Wait a moment for style to fully load, then add markers
    setTimeout(() => {
      if (state.allClinics.length > 0 || state.turkeyClinics.length > 0) {
        addMarkersToMap();
      } else {
        console.log('No clinics loaded yet, markers will be added after API response');
      }
    }, 500);
  }

  // Handle Map Resize
  function handleMapResize() {
    if (!state.map) return;

    const currentCenter = state.map.getCenter();
    setTimeout(() => {
      state.map.resize();
      state.map.setCenter(currentCenter);
    }, 300);
  }

  // Get Responsive Zoom Level
  function getResponsiveZoom(country = null) {
    const c = country || state.selectedCountry;
    const market = state.currentMarket;
    const countryKey = c === 'Turkey' ? 'turkey' : market;
    const width = window.innerWidth;

    let zoomLevel;
    if (width < 600) {
      zoomLevel = CONFIG.responsiveZoom.mobile[countryKey] || 3.9;
    } else if (width < 900) {
      zoomLevel = CONFIG.responsiveZoom.tablet[countryKey] || 4.2;
    } else {
      zoomLevel = CONFIG.responsiveZoom.desktop[countryKey] || 4.8;
    }

    return zoomLevel;
  }

  function getCountryConfig(country) {
    if (country === 'Turkey') {
      return {
        center: CONFIG.mapCenters.turkey.center,
        zoom: CONFIG.mapCenters.turkey.zoom
      };
    }

    const market = state.currentMarket;
    const storeConfig = CONFIG.mapCenters[market] || CONFIG.mapCenters.uk;
    return {
      center: storeConfig.center,
      zoom: storeConfig.zoom
    };
  }

  function getCountryLabel(market) {
    const labels = {
      'de': 'DE',
      'ie': 'IE',
      'uk': 'UK'
    };
    return labels[market || state.currentMarket] || 'UK';
  }

  function getFilteredClinics() {
    const clinics = state.selectedCountry === 'Turkey' ? state.turkeyClinics : state.allClinics;
    const searchTerm = document.getElementById('addressSearch')?.value || '';

    let filtered = clinics.filter(clinic =>
      clinic.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clinic.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (state.userLocation) {
      filtered = filtered.filter(clinic => {
        const distance = getDistance(
          state.userLocation.lat,
          state.userLocation.lng,
          parseFloat(clinic.latitude),
          parseFloat(clinic.longitude)
        );
        return distance <= state.radius;
      });
    }

    return filtered;
  }

  function getDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function addMarkersToMap() {
    if (!state.map || !state.map.isStyleLoaded()) return;

    document.querySelectorAll('.clinic-marker').forEach(m => m.remove());

    const filteredClinics = getFilteredClinics();

    if (state.userLocation) {
      const userMarkerEl = document.createElement('div');
      userMarkerEl.className = 'clinic-marker';
      userMarkerEl.style.width = '20px';
      userMarkerEl.style.height = '20px';
      userMarkerEl.style.borderRadius = '50%';
      userMarkerEl.style.backgroundColor = '#007bff';
      userMarkerEl.style.border = '3px solid white';
      userMarkerEl.style.cursor = 'pointer';

      new window.mapboxgl.Marker(userMarkerEl)
        .setLngLat([state.userLocation.lng, state.userLocation.lat])
        .addTo(state.map);
    }

    filteredClinics.forEach(clinic => {
      const el = document.createElement('div');
      el.className = 'clinic-marker';
      const isSelected = clinic.uuid === state.selectedClinic;

      el.style.width = isSelected ? '24px' : '20px';
      el.style.height = isSelected ? '24px' : '20px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = isSelected ? '#FFA500' : '#4100E6';
      el.style.cursor = 'pointer';
      el.style.border = '2px solid white';
      el.style.transition = 'all 0.3s ease';

      el.addEventListener('click', () => {
        selectClinic(clinic);
        if (state.map) {
          state.map.flyTo({
            center: [clinic.longitude, clinic.latitude],
            zoom: 14,
            essential: true
          });
        }
      });

      new window.mapboxgl.Marker(el)
        .setLngLat([clinic.longitude, clinic.latitude])
        .addTo(state.map);
    });

    if (state.userLocation && window.turf) {
      addRadiusCircle();
    }
  }

  function addRadiusCircle() {
    if (!state.map || !state.userLocation || !window.turf) return;

    const center = window.turf.point([state.userLocation.lng, state.userLocation.lat]);
    const circle = window.turf.circle(center, state.radius, { units: 'kilometers' });

    if (state.map.getSource('radius-circle')) {
      state.map.getSource('radius-circle').setData(circle);
    } else {
      state.map.addSource('radius-circle', {
        type: 'geojson',
        data: circle
      });

      state.map.addLayer({
        id: 'radius-circle-layer',
        type: 'fill',
        source: 'radius-circle',
        paint: {
          'fill-color': 'rgba(65, 0, 230, 0.1)',
          'fill-outline-color': '#4100E6'
        }
      });
    }
  }

  function geocodeAddress(address) {
    if (!address.trim()) return;

    if (state.geocodeTimeout) {
      clearTimeout(state.geocodeTimeout);
    }

    state.geocodeTimeout = setTimeout(() => {
      performGeocoding(address);
    }, 500);
  }

  function performGeocoding(address) {
    const encodedAddress = encodeURIComponent(address);
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${CONFIG.mapboxToken}`;

    fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          state.userLocation = { lat, lng };

          if (state.map) {
            state.map.flyTo({ center: [lng, lat], zoom: 10 });
          }

          addMarkersToMap();
          updateClinicList();
        } else {
          console.warn('Address not found:', address);
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error);
      });
  }

  function selectClinic(clinic) {
    state.selectedClinic = clinic.uuid;
    updateClinicList();
    addMarkersToMap();
  }

  function updateClinicList() {
    const clinicList = document.getElementById('clinicList');
    if (!clinicList) return;

    const filteredClinics = getFilteredClinics();

    if (filteredClinics.length === 0) {
      clinicList.innerHTML = `
        <div style="text-align: center; color: #999; padding: 2rem;">
          ${state.loading ? (state.currentMarket === 'de' ? 'Kliniken werden geladen...' : 'Loading clinics...') : (state.currentMarket === 'de' ? 'Keine Kliniken gefunden' : 'No clinics found')}
        </div>
      `;
      return;
    }

    clinicList.innerHTML = filteredClinics.map(clinic => `
      <div class="clinic-item ${clinic.uuid === state.selectedClinic ? 'selected' : ''}" data-clinic-id="${clinic.uuid}" style="
        padding: 1rem;
        border: 1px solid #ddd;
        border-radius: 0.5rem;
        cursor: pointer;
        transition: all 0.3s ease;
        background: ${clinic.uuid === state.selectedClinic ? '#f0e6ff' : 'white'};
      ">
        <div style="display: flex; align-items: center; margin-bottom: 0.5rem; gap: 0.5rem;">
          <div class="clinic-name" style="font-weight: bold; font-size: 1rem;">
            ${clinic.name}
          </div>
          ${clinic.is_preferred === 'yes' ? '<span style="color: #FFC107; font-size: 1.3rem;margin-top: -6px;">☆</span>' : ''}
        </div>
        <div class="clinic-address" style="color: #666; font-size: 0.9rem; margin-bottom: 0.75rem;">
          ${clinic.address}
        </div>
        <div style="display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;">
          ${state.currentMarket !== 'de' ? `
          ${hasSonsPage(clinic) ? `
          <a class="clinic-link" href="${getDetailUrl(clinic)}" style="
            display: inline-flex;
            align-items: center;
            color: #4100E6;
            text-decoration: none;
            font-size: 0.9rem;
            margin-top: 0.75rem;
            transition: color 0.3s ease;
            font-weight: 500;
          ">
            View clinic page
            <span style="margin-left: 0.5rem; transition: transform 0.3s ease;">→</span>
          </a>
          ` : ''}
          ${clinic.website_url?.trim() && !hasSonsPage(clinic) ? `
          <a class="clinic-website-link" href="${clinic.website_url}" target="_blank" rel="noopener noreferrer" style="
            display: inline-flex;
            align-items: center;
            color: #4100E6;
            text-decoration: none;
            font-size: 0.9rem;
            margin-top: 0.75rem;
            transition: color 0.3s ease;
            font-weight: 500;
          ">
            Visit clinic website
            <span style="margin-left: 0.5rem; transition: transform 0.3s ease;">→</span>
          </a>
          ` : ''}
          ` : `
          <a class="clinic-link" href="${clinic.website_url?.trim() ? clinic.website_url : getDetailUrl(clinic)}" target="_blank" rel="noopener noreferrer" style="
            display: inline-flex;
            align-items: center;
            color: #4100E6;
            text-decoration: none;
            font-size: 0.9rem;
            margin-top: 0.75rem;
            transition: color 0.3s ease;
            font-weight: 500;
          ">
            Details ansehen
            <span style="margin-left: 0.5rem; transition: transform 0.3s ease;">→</span>
          </a>
          `}
        </div>
      </div>
    `).join('');
  }

  // Resolve the internal link destination for a clinic. Slugs with dedicated
  // Sons pages link there; everything else falls back to the internal
  // clinics/<slug> page on the API host.
  function getDetailUrl(clinic) {
    const slugOverrides = {
      'ksl-clinic': window.location.origin + '/pages/ksl-clinic',
      'hq-hair-transplants': window.location.origin + '/pages/hq-clinic',
      'ksl-clinic-dublin': window.location.origin + '/pages/ksl-clinic'
    };
    if (clinic.clinic_slug === 'want-hair' && state.currentMarket === 'uk') {
      return window.location.origin + '/pages/wanthair';
    }
    return clinic.clinic_slug && slugOverrides[clinic.clinic_slug]
      ? slugOverrides[clinic.clinic_slug]
      : state.customiserApi + '/clinics/' + clinic.clinic_slug;
  }

  // Clinics with a dedicated Sons page get an internal "View clinic page" link.
  function hasSonsPage(clinic) {
    const slugsWithPages = ['ksl-clinic', 'hq-hair-transplants'];
    if (clinic.clinic_slug === 'want-hair' && state.currentMarket === 'uk') return true;
    return slugsWithPages.includes(clinic.clinic_slug);
  }

  // Delegated click handler: works for SSR-rendered cards and JS re-renders alike.
  function handleClinicListClick(e) {
    const item = e.target.closest('.clinic-item');
    if (!item) return;
    if (e.target.closest('.clinic-link') || e.target.closest('.clinic-website-link')) return;

    const clinicId = item.dataset.clinicId;
    const clinics = state.selectedCountry === 'Turkey' ? state.turkeyClinics : state.allClinics;
    const clinic = clinics.find(c => c.uuid === clinicId);
    if (!clinic) return;

    selectClinic(clinic);
    if (state.map) {
      state.map.flyTo({
        center: [clinic.longitude, clinic.latitude],
        zoom: 8
      });
    }
  }

  function updateUI() {
    updateClinicList();
    updateButtonStates();
  }

  function updateButtonStates() {
    document.querySelectorAll('.clinic-btn').forEach(btn => {
      const country = btn.dataset.country;
      if (country === 'default') {
        btn.classList.toggle('active', state.selectedCountry === 'default');
        btn.style.backgroundColor = state.selectedCountry === 'default' ? '#000000' : '#4100E6';
      } else if (country === 'Turkey') {
        btn.classList.toggle('active', state.selectedCountry === 'Turkey');
        btn.style.backgroundColor = state.selectedCountry === 'Turkey' ? '#000000' : '#4100E6';
      }
    });
  }

  function clearSearch() {
    const searchInput = document.getElementById('addressSearch');
    const searchInputMobile = document.getElementById('addressSearchMobile');

    if (searchInput) searchInput.value = '';
    if (searchInputMobile) searchInputMobile.value = '';

    state.userLocation = null;
    state.selectedClinic = null;
    state.radius = CONFIG.defaultRadius;

    if (state.map) {
      const countryConfig = getCountryConfig(state.selectedCountry);
      const zoomLevel = getResponsiveZoom();

      state.map.flyTo({
        center: countryConfig.center,
        zoom: zoomLevel,
        essential: true
      });

      if (state.map.getLayer('radius-circle-layer')) {
        state.map.removeLayer('radius-circle-layer');
      }
      if (state.map.getSource('radius-circle')) {
        state.map.removeSource('radius-circle');
      }
    }

    updateUI();
    addMarkersToMap();
  }

  function switchCountry(country) {
    state.selectedCountry = country;
    state.userLocation = null;
    state.selectedClinic = null;

    if (state.map) {
      const countryConfig = getCountryConfig(country);
      const zoomLevel = getResponsiveZoom(country);

      state.map.flyTo({
        center: countryConfig.center,
        zoom: zoomLevel,
        essential: true
      });

      if (state.map.getLayer('radius-circle-layer')) {
        state.map.removeLayer('radius-circle-layer');
      }
      if (state.map.getSource('radius-circle')) {
        state.map.removeSource('radius-circle');
      }
    }

    updateUI();
    addMarkersToMap();
  }

  function initEventListeners() {
    const searchInput = document.getElementById('addressSearch');
    const clearBtn = document.getElementById('clearSearch');
    const clearBtn1 = document.getElementById('clearSearch1');
    const searchInputMobile = document.getElementById('addressSearchMobile');

    // Delegated clinic selection - covers SSR cards and any JS re-render
    const clinicList = document.getElementById('clinicList');
    if (clinicList) {
      clinicList.addEventListener('click', handleClinicListClick);
    }

    function syncSearchInputs(value) {
      if (searchInput && searchInput.value !== value) {
        searchInput.value = value;
      }
      if (searchInputMobile && searchInputMobile.value !== value) {
        searchInputMobile.value = value;
      }

      // Show/hide both clear buttons
      if (clearBtn) {
        clearBtn.style.display = value.length > 0 ? 'block' : 'none';
      }
      if (clearBtn1) {
        clearBtn1.style.display = value.length > 0 ? 'block' : 'none';
      }
    }

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        const value = e.target.value.trim();
        syncSearchInputs(value);

        // Search after 3 characters
        if (value.length >= 3) {
          geocodeAddress(value);
        }
      });

      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && searchInput.value.trim().length > 0) {
          geocodeAddress(searchInput.value);
        }
      });
    }

    if (searchInputMobile) {
      searchInputMobile.addEventListener('input', (e) => {
        const value = e.target.value.trim();
        syncSearchInputs(value);

        // Search after 3 characters
        if (value.length >= 3) {
          geocodeAddress(value);
        }
      });

      searchInputMobile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && searchInputMobile.value.trim().length > 0) {
          geocodeAddress(searchInputMobile.value);
        }
      });
    }

    // Clear buttons for both desktop and mobile
    if (clearBtn) {
      clearBtn.addEventListener('click', clearSearch);
    }
    if (clearBtn1) {
      clearBtn1.addEventListener('click', clearSearch);
    }

    document.querySelectorAll('.clinic-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const country = btn.dataset.country;
        if (country === 'default') {
          switchCountry('default');
        } else {
          switchCountry(country);
        }
      });
    });

    const radiusSlider = document.getElementById('radiusSlider');
    if (radiusSlider) {
      radiusSlider.addEventListener('input', (e) => {
        state.radius = parseFloat(e.target.value);
        document.getElementById('radiusValue').textContent = state.radius;
        updateClinicList();
        addMarkersToMap();
      });
    }
  }

  // Load clinic data from the SSR-embedded JSON (no API call needed)
  function loadSSRClinicData() {
    let ssrData = document.getElementById('ssrClinicData');
    let tkData = document.getElementById('ssrTurkeyClinicData');

    try {
      if (ssrData) {
        let clinics = JSON.parse(ssrData.textContent);
        if (Array.isArray(clinics) && clinics.length > 0) {
          state.allClinics = clinics.map(function(clinic) {
            clinic.country = getCountryLabel(state.currentMarket);
            return clinic;
          });
          console.log('Loaded clinics from SSR data:', state.allClinics.length);
        }
      }

      if (tkData) {
        let tkClinics = JSON.parse(tkData.textContent);
        if (Array.isArray(tkClinics) && tkClinics.length > 0) {
          state.turkeyClinics = tkClinics;
          console.log('Loaded Turkey clinics from SSR data:', state.turkeyClinics.length);
        }
      }
    } catch (e) {
      console.error('Error parsing SSR clinic data:', e);
    }
  }

  function init() {
    console.log('Clinic Finder: Initializing...');
    console.log('State:', {
      storeId: state.storeId,
      currentMarket: state.currentMarket,
      mapboxToken: CONFIG.mapboxToken.substring(0, 10) + '...'
    });

    try {
      initEventListeners();
      console.log('Event listeners initialized');
    } catch (error) {
      console.error('Error initializing event listeners:', error);
    }

    loadSSRClinicData();

    if (!window.mapboxgl) {
      console.warn('Mapbox GL JS not loaded yet, retrying in 500ms...');
      setTimeout(init, 500);
      return;
    }

    try {
      initMapbox();
      console.log('Map initialized');
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  console.log('Clinic Finder: Script loaded, waiting for DOM...');
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      console.log('DOM ready, initializing clinic finder...');
      init();
    });
  } else {
    console.log('DOM already ready, initializing clinic finder...');
    init();
  }
})();
